import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalHeader, NavigationBar, XPBar } from '@puzzlemania/lobby';
import { SettingsPopup, ToastNotification } from '@puzzlemania/ui';
import { progressionStore, xpRequiredForLevel } from '@puzzlemania/progression';
import { FEATURE_FLAG_KEYS, remoteConfigStore } from '@puzzlemania/remote-config';
import { SettingsSaveModule, persistChosenLanguage } from '@puzzlemania/shared';
import { Outlet, useLocation } from 'react-router-dom';
import { coinIconUrl } from './assets';
import { usePlatformServices } from './composition/PlatformServicesContext';
import { useShopPopupStore } from './state/shopPopupStore';
import { createHostFullscreen, hostAllows } from './lib/fullscreen';
import { getPlatformCapabilities } from '../platforms/capabilities';

// Local fallback curve constants — mirrors ProfileScreen.tsx's own choice (WP-XP.3 added
// the matching XP_CURVE_BASE/XP_CURVE_INCREMENT remote-config keys, not yet read here).
const XP_CURVE_BASE = 100;
const XP_CURVE_INCREMENT = 50;

// The Lobby's full-screen toggle: the same permission the games get (the
// profile names this host, and the browser allows it). Built once.
const lobbyFullscreen = createHostFullscreen(
  hostAllows(getPlatformCapabilities().fullscreenButton?.hosts, undefined),
);

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const [toast, setToast] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(() => SettingsSaveModule.load());
  const platform = usePlatformServices();

  const xp = progressionStore((s) => s.xp);
  const level = progressionStore((s) => s.level);
  const lastLevelUp = progressionStore((s) => s.lastLevelUp);
  const xpToNext = xpRequiredForLevel(level, XP_CURVE_BASE, XP_CURVE_INCREMENT);
  // Reactive to a live RC refresh, matching GameGrid's own flag-check pattern.
  const liveFlags = remoteConfigStore((s) => s.flags);
  const xpEnabled =
    liveFlags[FEATURE_FLAG_KEYS.XP_ENABLED] ??
    platform.remoteConfig.getBoolean(FEATURE_FLAG_KEYS.XP_ENABLED, true);

  useEffect(() => {
    const navState = state as { toast?: string } | null;
    if (navState?.toast) {
      setToast(navState.toast);
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [state]);

  return (
    <div className="app">
      <GlobalHeader
        coinIconUrl={coinIconUrl}
        onShopClick={() => useShopPopupStore.getState().open('lobby')}
        onSettingsClick={() => setIsSettingsOpen(true)}
        {...(lobbyFullscreen ? { fullscreen: lobbyFullscreen } : {})}
      />
      {xpEnabled && <XPBar level={level} xp={xp} xpToNext={xpToNext} />}
      <main className="app-content">
        <Outlet />
      </main>
      <NavigationBar />
      {toast && (
        <div className="simple-toast" role="status" data-testid="global-toast">
          {toast}
        </div>
      )}
      {xpEnabled && lastLevelUp !== null && (
        <ToastNotification
          message={t('profile.levelUp', { level: lastLevelUp })}
          type="success"
          onDismiss={() => progressionStore.setState({ lastLevelUp: null })}
        />
      )}
      <SettingsPopup
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        musicVolume={settings.musicVolume}
        sfxVolume={settings.sfxVolume}
        onMusicVolumeChange={(value) => {
          setSettings((s) => ({ ...s, musicVolume: value }));
          // Through the real AudioService, not straight to storage (2026-08-05
          // audit): setVolume() both persists AND reaches whatever is already
          // sounding, which a direct SettingsSaveModule.save() never did.
          platform.audio.setVolume('music', value);
        }}
        onSfxVolumeChange={(value) => {
          setSettings((s) => ({ ...s, sfxVolume: value }));
          platform.audio.setVolume('sfx', value);
        }}
        language={settings.language}
        onLanguageChange={(lang) => {
          setSettings((s) => ({ ...s, language: lang }));
          // As chosen, not a raw write — a raw `{ language }` reads as a
          // supersedable detection since Module 59.
          persistChosenLanguage(lang);
          void i18n.changeLanguage(lang);
        }}
        onNavigatePrivacy={() => platform.openLegalDoc('privacy', 'lobby')}
        onNavigateTerms={() => platform.openLegalDoc('terms', 'lobby')}
        appVersion={__APP_VERSION__}
      />
    </div>
  );
}
